import React from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import OfficeBuildingOutlineIcon from 'mdi-react/OfficeBuildingOutlineIcon';
import LinkOffIcon from 'mdi-react/LinkOffIcon';
import * as listStyles from '../../admin-list.styles';

interface Props {
  readonly officeNames: string[];
  /** How many names fit before the rest is summarised as "+N more". */
  readonly maxVisible?: number;
  /** Inline placement (next to other content) drops the spacing a stacked chip row needs. */
  readonly inline?: boolean;
}

// Past a handful the chips stop being scannable and start pushing the row apart, so the tail is
// summarised instead.
const DEFAULT_MAX_VISIBLE_CHIPS = 3;

// Which offices a parking area or facility belongs to. Nothing linked is worth flagging: the item
// exists but nobody can book it, so it gets a warning chip instead of an empty row.
const LinkedOfficeChips = (
  { officeNames, maxVisible = DEFAULT_MAX_VISIBLE_CHIPS, inline = false }: Props,
): React.JSX.Element => {
  const { t } = useTranslation();
  const containerClass = style(inline ? listStyles.inlineChipRow : listStyles.chipRow);

  if (officeNames.length === 0) {
    return (
      <div className={containerClass}>
        <span className={style(listStyles.chip('warning'))}>
          <span className={style(listStyles.chipIcon)}>
            <LinkOffIcon size={13} />
          </span>
          {t('not-linked')}
        </span>
      </div>
    );
  }

  const visibleNames = officeNames.slice(0, maxVisible);
  const hiddenCount = officeNames.length - visibleNames.length;

  return (
    <div className={containerClass}>
      {visibleNames.map((officeName) => (
        <span className={style(listStyles.chip('muted'))} key={officeName} title={officeName}>
          <span className={style(listStyles.chipIcon)}>
            <OfficeBuildingOutlineIcon size={13} />
          </span>
          {officeName}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span className={style(listStyles.chip('muted'))} title={officeNames.join(', ')}>
          {(t('and-more-count') ?? '').replace('[x]', hiddenCount.toString())}
        </span>
      )}
    </div>
  );
};

export default LinkedOfficeChips;
